import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { SidebarService } from '../../shared/components/sidebar/sidebar.service';

export interface ChartSizeBreakpoint {
  /** Chart diameter in pixels */
  size: number;
  /** Chart ring thickness in pixels */
  thickness: number;
}

interface ResponsiveChartSizeBreakpoint {
  /** Size when sidebar is collapsed */
  collapsed: ChartSizeBreakpoint;
  /** Size when sidebar is expanded */
  expanded: ChartSizeBreakpoint;
}

interface ChartSizeConfig {
  /** Mobile devices (< 640px) */
  mobile: ChartSizeBreakpoint;
  /** Tablet devices (640px - 1023px) */
  tablet: ChartSizeBreakpoint;
  /** Large screens (1024px - 1199px) - sidebar aware */
  lg: ResponsiveChartSizeBreakpoint;
  /** Large extended screens (1200px - 1279px) - sidebar aware */
  lgExtended: ResponsiveChartSizeBreakpoint;
  /** Extra large start (1280px - 1339px) - sidebar aware */
  xlStart: ResponsiveChartSizeBreakpoint;
  /** Extra large (1340px - 1439px) - sidebar aware */
  xl: ResponsiveChartSizeBreakpoint;
  /** Desktop (>= 1440px) */
  desktop: ChartSizeBreakpoint;
  /** Fallback if window is undefined */
  fallback: ChartSizeBreakpoint;
}

/** Chart size configuration for collapsible sections */
const COLLAPSIBLE_SECTION_CHART_SIZES: ChartSizeConfig = {
  mobile: { size: 280, thickness: 44 },
  tablet: { size: 260, thickness: 48 },
  lg: {
    collapsed: { size: 340, thickness: 50 },
    expanded: { size: 260, thickness: 44 },
  },
  lgExtended: {
    collapsed: { size: 350, thickness: 52 },
    expanded: { size: 300, thickness: 46 },
  },
  xlStart: {
    collapsed: { size: 360, thickness: 54 },
    expanded: { size: 320, thickness: 48 },
  },
  xl: {
    collapsed: { size: 370, thickness: 54 },
    expanded: { size: 340, thickness: 48 },
  },
  desktop: { size: 400, thickness: 56 },
  fallback: { size: 260, thickness: 48 },
};

/** Chart size configuration for impact overview */
const IMPACT_OVERVIEW_CHART_SIZES: ChartSizeConfig = {
  mobile: { size: 280, thickness: 44 },
  tablet: { size: 260, thickness: 48 },
  lg: {
    collapsed: { size: 260, thickness: 46 },
    expanded: { size: 200, thickness: 40 },
  },
  lgExtended: {
    collapsed: { size: 280, thickness: 48 },
    expanded: { size: 240, thickness: 42 },
  },
  xlStart: {
    collapsed: { size: 300, thickness: 50 },
    expanded: { size: 260, thickness: 44 },
  },
  xl: {
    collapsed: { size: 320, thickness: 50 },
    expanded: { size: 280, thickness: 44 },
  },
  desktop: { size: 380, thickness: 56 },
  fallback: { size: 260, thickness: 48 },
};

const CHART_SIZE_CONFIGS = {
  collapsibleSection: COLLAPSIBLE_SECTION_CHART_SIZES,
  impactOverview: IMPACT_OVERVIEW_CHART_SIZES,
} as const satisfies Record<string, ChartSizeConfig>;

/** Chart variants components can ask dimensions for */
export type ChartSizeVariant = keyof typeof CHART_SIZE_CONFIGS;

/** Screen size breakpoints in pixels */
const SCREEN_BREAKPOINTS = {
  mobile: 640,
  tablet: 1024,
  lg: 1200,
  lgExtended: 1280,
  xlStart: 1340,
  xl: 1440,
} as const;

function resolveDimensions(
  config: ChartSizeConfig,
  windowWidth: number,
  sidebarCollapsed: boolean,
): ChartSizeBreakpoint {
  if (windowWidth < SCREEN_BREAKPOINTS.mobile) {
    return config.mobile;
  }
  if (windowWidth < SCREEN_BREAKPOINTS.tablet) {
    return config.tablet;
  }
  if (windowWidth < SCREEN_BREAKPOINTS.lg) {
    return sidebarCollapsed ? config.lg.collapsed : config.lg.expanded;
  }
  if (windowWidth < SCREEN_BREAKPOINTS.lgExtended) {
    return sidebarCollapsed
      ? config.lgExtended.collapsed
      : config.lgExtended.expanded;
  }
  if (windowWidth < SCREEN_BREAKPOINTS.xlStart) {
    return sidebarCollapsed
      ? config.xlStart.collapsed
      : config.xlStart.expanded;
  }
  if (windowWidth < SCREEN_BREAKPOINTS.xl) {
    return sidebarCollapsed ? config.xl.collapsed : config.xl.expanded;
  }
  return config.desktop;
}

/**
 * Resolves responsive doughnut chart dimensions.
 *
 * Owns the two reactive inputs the sizing depends on — window width and sidebar
 * state — so components only name the chart variant they render.
 * Both are signals: reading `getSize()` / `getThickness()` from a template keeps
 * the chart in sync when the window is resized or the sidebar is toggled.
 */
@Injectable({ providedIn: 'root' })
export class ChartSizeService {
  private sidebarService = inject(SidebarService);
  private destroyRef = inject(DestroyRef);

  /** `null` when there is no window (SSR) — callers fall back to the config default. */
  private windowWidth = signal<number | null>(
    typeof window !== 'undefined' ? window.innerWidth : null,
  );

  constructor() {
    if (typeof window === 'undefined') return;

    const onResize = () => this.windowWidth.set(window.innerWidth);
    window.addEventListener('resize', onResize);
    this.destroyRef.onDestroy(() =>
      window.removeEventListener('resize', onResize),
    );
  }

  getDimensions(variant: ChartSizeVariant): ChartSizeBreakpoint {
    const config = CHART_SIZE_CONFIGS[variant];
    const width = this.windowWidth();

    if (width === null) return config.fallback;

    return resolveDimensions(
      config,
      width,
      this.sidebarService.isCollapsedSignal(),
    );
  }

  getSize(variant: ChartSizeVariant): number {
    return this.getDimensions(variant).size;
  }

  getThickness(variant: ChartSizeVariant): number {
    return this.getDimensions(variant).thickness;
  }
}
